import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import theme from '../styles/theme'
import { getUserDeposits } from '../services/api'

const PointLogContainer = styled.div`
  background: ${theme.color.background.light};
  padding: 2rem;
  border-radius: 8px;
  box-shadow: inset 0 2px 4px ${theme.color.shadow.light};
  margin-top: 2rem;
`

const Title = styled.h3`
  font-size: 2rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin-bottom: 1.5rem;
`

const FiltersContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  gap: 1rem;
  flex-wrap: wrap;
`

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`

const Select = styled.select`
  padding: 0.8rem;
  border: 2px solid ${theme.color.border.light};
  border-radius: 6px;
  font-size: 1.4rem;
  background: ${theme.color.background.frame};
  color: ${theme.color.text.base};
  min-width: 180px;
  
  &:focus {
    outline: none;
    border-color: ${theme.color.accent.primary};
  }
`

const DateInput = styled.input`
  padding: 0.8rem;
  border: 2px solid ${theme.color.border.light};
  border-radius: 6px;
  font-size: 1.4rem;
  background: ${theme.color.background.frame};
  color: ${theme.color.text.base};
  
  &:focus {
    outline: none;
    border-color: ${theme.color.accent.primary};
  }
`

const LogContainer = styled.div`
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid ${theme.color.border.light};
  border-radius: 6px;
  background: ${theme.color.background.frame};
`

const LogEntry = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid ${theme.color.border.light};
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: ${theme.color.background.lightGrey};
  }
`

const LogInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`

const PointSource = styled.div`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${theme.color.text.base};
`

const DateTime = styled.div`
  font-size: 1.2rem;
  color: ${theme.color.text.grey};
`

const PointsIssued = styled.div`
  font-size: 1.6rem;
  font-weight: 700;
  color: ${theme.color.accent.primary};
`

const NoDataMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${theme.color.text.grey};
  font-size: 1.4rem;
`

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${theme.color.text.grey};
  font-size: 1.4rem;
`

interface PointLogProps {
  userData: any
}

interface PointDeposit {
  event_stream_payload: {
    Modification: number
    PointAccountName: string
    PointSourceName: string
    TimeOfOccurrence: string
  }
}

const ALLOWED_POINT_ACCOUNTS = [
  'Social',
  "Spend on Barry's X",
  'Spend on Retail or Fuel',
  'Take a Class'
]

const PointLog: React.FC<PointLogProps> = ({ userData }) => {
  const [deposits, setDeposits] = useState<PointDeposit[]>([])
  const [filteredDeposits, setFilteredDeposits] = useState<PointDeposit[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedAccount, setSelectedAccount] = useState<string>('All')
  const [dateFilter, setDateFilter] = useState<string>('')

  useEffect(() => {
    if (userData?.user?.external_id) {
      loadPointDeposits()
    }
  }, [userData?.user?.external_id])

  useEffect(() => {
    applyFilters()
  }, [deposits, selectedAccount, dateFilter])

  const loadPointDeposits = async () => {
    if (!userData?.user?.external_id) return
    
    setIsLoading(true)
    try {
      const response = await getUserDeposits(userData.user.external_id)
      const events = response.result || []
      
      // Filter for allowed point account names
      const filteredEvents = events.filter((event: PointDeposit) => 
        ALLOWED_POINT_ACCOUNTS.includes(event.event_stream_payload?.PointAccountName)
      )
      
      setDeposits(filteredEvents)
    } catch (error) {
      console.error('Failed to load point deposits:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const applyFilters = () => {
    let filtered = [...deposits]

    // Filter by point account
    if (selectedAccount !== 'All') {
      filtered = filtered.filter(deposit => 
        deposit.event_stream_payload?.PointAccountName === selectedAccount
      )
    }

    // Filter by date
    if (dateFilter) {
      const filterDate = new Date(dateFilter)
      filtered = filtered.filter(deposit => {
        const depositDate = new Date(deposit.event_stream_payload?.TimeOfOccurrence)
        return depositDate.toDateString() === filterDate.toDateString()
      })
    }

    setFilteredDeposits(filtered)
  }

  const formatDateTime = (isoString: string) => {
    if (!isoString) return 'N/A'
    
    try {
      const date = new Date(isoString)
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const year = String(date.getFullYear()).slice(-2)
      
      let hours = date.getHours()
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const ampm = hours >= 12 ? 'PM' : 'AM'
      hours = hours % 12
      hours = hours ? hours : 12
      
      return `${month}/${day}/${year} ${hours}:${minutes}${ampm} EST`
    } catch {
      return 'Invalid Date'
    }
  }

  const getPointSourceDisplay = (deposit: PointDeposit) => {
    const sourceName = deposit.event_stream_payload?.PointSourceName
    const accountName = deposit.event_stream_payload?.PointAccountName
    
    if (sourceName && sourceName.trim()) {
      return sourceName
    }
    
    return accountName || 'Unknown Source'
  }


  return (
    <PointLogContainer>
      <Title>Point Log</Title>
      
      <FiltersContainer>
        <FilterGroup>
          <Select 
            value={selectedAccount} 
            onChange={(e) => setSelectedAccount(e.target.value)}
          >
            <option value="All">All Point Accounts</option>
            {ALLOWED_POINT_ACCOUNTS.map(account => (
              <option key={account} value={account}>{account}</option>
            ))}
          </Select>
        </FilterGroup>
        
        <FilterGroup>
          <DateInput
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            placeholder="Filter by date"
          />
        </FilterGroup>
      </FiltersContainer>

      <LogContainer>
        {isLoading ? (
          <LoadingMessage>Loading point deposits...</LoadingMessage>
        ) : filteredDeposits.length === 0 ? (
          <NoDataMessage>
            {deposits.length === 0 ? 'No point deposits found' : 'No deposits match the selected filters'}
          </NoDataMessage>
        ) : (
          filteredDeposits.map((deposit, index) => (
            <LogEntry key={index}>
              <LogInfo>
                <PointSource>{getPointSourceDisplay(deposit)}</PointSource>
                <DateTime>{formatDateTime(deposit.event_stream_payload?.TimeOfOccurrence)}</DateTime>
              </LogInfo>
              <PointsIssued>+{deposit.event_stream_payload?.Modification || 0}</PointsIssued>
            </LogEntry>
          ))
        )}
      </LogContainer>
    </PointLogContainer>
  )
}

export default PointLog